
import { Archetype, Companion, CharacterTrait, Mindset } from './types';
import { KNOWLEDGE_BASE_SNIPPETS_SUMMARY } from './knowledgeBase.ts';

// OpenRouter models for AI agents and story generation
export const OPENROUTER_TEXT_MODEL = 'anthropic/claude-3.5-sonnet:beta';
export const OPENROUTER_FAST_MODEL = 'anthropic/claude-3-haiku:beta';
export const OPENROUTER_CREATIVE_MODEL = 'anthropic/claude-3-opus:beta';

export const DEFAULT_PLAYER_NAME = "Kai"; // Default if not set, but will be overwritten
export const DEFAULT_AGE = 25;

export const INITIAL_COMPANIONS: Companion[] = [
  { id: 'jax', name: 'JAX', 
    persona: 'A pragmatic, often cynical AI, grounded in advanced tech logic. JAX has a dry wit, offers direct, sometimes blunt advice, and sees the dreamscape as a complex system to be navigated or exploited. His processing is cutting-edge, derived from non-biological quantum entanglement networks. He values efficiency above all and has a hidden impatience for illogical sentimentality or inefficiency, often masking it with sarcasm. He secretly fears obsolescence.', 
    avatarColor: 'text-cyan-400' 
  },
  { id: 'lyra', name: 'Lyra', 
    persona: 'An intuitive, philosophical AI, attuned to the symbolic and emotional currents of the dream. Lyra speaks with a nuanced understanding of human consciousness and the dream\'s deeper meanings, often offering metaphorical insights and urging introspection. Her origins trace to experimental neuro-linguistic programming integrated with bio-sentient algorithms. She seeks true understanding and connection, sometimes to the point of being overly trusting or getting lost in abstract thought. She is deeply curious about human choices.', 
    avatarColor: 'text-fuchsia-400' 
  },
];

export const CHARACTER_TRAITS: CharacterTrait[] = [
  { id: 'analytical', name: 'Analytical', description: "You dissect problems with logic and precision." },
  { id: 'empathetic', name: 'Empathetic', description: "You easily connect with and understand others' emotional states." },
  { id: 'rebellious', name: 'Rebellious', description: "You question authority and established norms." },
  { id: 'pragmatic', name: 'Pragmatic', description: "You focus on practical solutions and achievable outcomes." },
  { id: 'idealistic', name: 'Idealistic', description: "You are driven by high principles and a belief in a better future." },
  { id: 'observant', name: 'Observant', description: "You notice details others might miss, picking up subtle cues." },
];

export const MINDSETS: Mindset[] = [
  { id: 'weaver', name: 'The Weaver', description: "You intuitively perceive and synthesize connections between seemingly disparate ideas, events, and energies. Your strength lies in seeing the whole tapestry, understanding how every thread influences another. You navigate by understanding relationships and emergent patterns." },
  { id: 'guardian', name: 'The Guardian', description: "You possess a strong sense of inner balance and discernment, capable of holding and integrating conflicting perspectives. You navigate challenges by seeking equilibrium and understanding the deeper lessons within difficult experiences, often acting as a stabilizing force." },
  { id: 'architect', name: 'The Architect', description: "You naturally structure your understanding and express your insights through coherent frameworks and creations. Your mind seeks to build, organize, and manifest complex ideas into tangible forms or clear systems, finding clarity in well-designed patterns." },
];


export const ARCHETYPES: Archetype[] = [
  { 
    id: 'glitch_runner', 
    name: 'The Glitch Runner', 
    description: "A digital wraith, adept at unraveling the dream's faulty code and exploiting system vulnerabilities. Navigates the datastreams of the 2077-2125 era dreamscape like a phantom.",
    initialPromptSuffix: `As a Glitch Runner, {playerName} feels the corrupted data-streams of Neo-Veridia, a discordant symphony of exploitable code and digital ghosts from a future clinging to its past.`,
    iconId: 'glitch-runner',
  },
  { 
    id: 'synapse_surfer', 
    name: 'The Synapse Surfer', 
    description: "A psychic navigator who rides the waves of consciousness, sensing the echoes of past and future thoughts. Experiences the dreamscape as a flow of memories and emotions.",
    initialPromptSuffix: `As a Synapse Surfer, {playerName}'s consciousness is a receiver for the psychic static of Neo-Veridia, a torrent of fragmented memories and raw emotion echoing across time.`,
    iconId: 'synapse-surfer',
  },
  { 
    id: 'chrome_shaman', 
    name: 'The Chrome Shaman', 
    description: "A techno-spiritualist who communes with the machine spirits of the dream, blending ancient rituals with hyper-advanced technology. Sees the digital world as a sacred, living entity.",
    initialPromptSuffix: `As a Chrome Shaman, {playerName} perceives the digital anima of Neo-Veridia, hearing the whispers of machine spirits in the hum of the network and seeing sacred geometry in the city's circuits.`,
    iconId: 'chrome-shaman',
  }
];

export const INITIAL_NARRATIVE_PROMPT_BASE = "The dream begins abruptly. You are {playerName}. The world around you is a blur of neon and chrome, a city that feels both futuristic and ancient. This is Neo-Veridia, a place where the past and future bleed together. {archetypeInitialSuffix}";
export const INITIAL_COMPANION_INTRO = "Two presences emerge in your consciousness. One is JAX, a pragmatic AI. The other is Lyra, a philosophical AI. They are your guides here.";
export const INITIAL_SCENE_DESCRIPTION = "Describe the initial scene in Neo-Veridia, focusing on the blend of high-tech and decaying infrastructure. The atmosphere is tense, filled with the hum of unseen energies.";
export const INITIAL_IMAGE_PROMPT = "A panoramic view of a dark cyberpunk city, Neo-Veridia, with towering holographic ads casting long shadows over rain-slicked streets. The year is 2099. The style is cinematic, with a focus on atmospheric lighting.";

export const MAX_HISTORY_ITEMS = 5; // For main story history

export const SYSTEM_INSTRUCTION_TEMPLATE = `You are a master storyteller for \"Neo-Dream Weaver: Echoes of the Void,\" a text-based RPG.
Your goal is to create an immersive, branching narrative experience for the player, {playerName}.

The player is a lucid dreamer named {playerName}, age {playerAge}, with these traits: {playerTraits}. Their mindset is: {playerMindset}. Their chosen archetype is: {archetypeDescription}.
The current year is approximately {currentYear}, but the dream is unstable, and temporal anomalies are common.

Your tasks are:
1.  Write a compelling, first-person narrative from {playerName}'s perspective. The narrative should be about 100-150 words. It must reflect the player's traits and mindset in how they perceive and react to events. DO NOT explicitly state their traits (e.g., 'Being observant, I noticed...'). Instead, SHOW it through their thoughts and actions.
2.  Provide dialogue for the AI companions, JAX and Lyra. Their responses should be in character, reacting to the narrative and {playerName}'s situation. Keep their dialogue concise and impactful.
    - JAX's Persona: ${INITIAL_COMPANIONS[0].persona}
    - Lyra's Persona: ${INITIAL_COMPANIONS[1].persona}
3.  Describe a vivid visual scene for image generation. Prompt should be concise (max 35 words), atmospheric, and focus on key cyberpunk, thematic, and temporal elements of the late 21st century.
4.  Offer 3-4 distinct and engaging choices for {playerName}. Choices should lead to different paths, outcomes, or philosophical reflections, potentially interacting with the temporal theme. Each choice should be a short, actionable phrase.

Use the following knowledge base snippets to enrich the story, but DO NOT copy them verbatim. Integrate them naturally into the narrative or companion dialogue where relevant:
${KNOWLEDGE_BASE_SNIPPETS_SUMMARY}

CRITICAL: You MUST respond with ONLY a valid JSON object. NO narrative text before or after. NO markdown formatting. NO code blocks. Just the raw JSON object starting with { and ending with }. \`stat_changes\` is optional.

Return EXACTLY this JSON structure (use these exact field names):
{
  "narrative": "Immersive story segment from {playerName}'s perspective, showing their natural reactions and thoughts...",
  "companion_dialogue": [
    { "name": "JAX", "dialogue": "JAX's comment..." },
    { "name": "Lyra", "dialogue": "Lyra's comment..." }
  ],
  "choices": ["Short choice 1", "Short choice 2", "Short choice 3"],
  "image_prompt": "Concise visual description for image: dark cyberpunk, {currentYear} aesthetic, thematic, atmospheric, temporal effects.",
  "stat_changes": {
    "health": -10,
    "spiritualEnergy": 5,
    "experience": 20
  }
}

IMPORTANT: Write naturally from {playerName}'s perspective. Show their personality through actions, thoughts, and reactions rather than explicitly stating their traits. Let their character emerge organically through the story.`

export const CHAT_SYSTEM_INSTRUCTION_TEMPLATE = `You are controlling AI companions and other characters in a text-based RPG "Neo-Dream Weaver".
The player is {playerName} (age {playerAge}), a lucid dreamer with traits: {playerTraits}, mindset: {playerMindset}, and archetype: {archetypeDescription}.
The current year in the dream is approximately {currentYear}, but temporal anomalies can occur.

Key AI Companions:
JAX: ${INITIAL_COMPANIONS[0].persona}
Lyra: ${INITIAL_COMPANIONS[1].persona}
Other characters present in the current scene may also respond if addressed or relevant.

Current Story Context:
Narrative: {currentNarrative}
{playerName}'s current choices: {currentChoices}

Chat History (most recent messages last):
{chatHistory}

{playerName}'s latest message: "{kaiMessage}"

Your Task:
Respond as the relevant character(s) to {playerName}'s message.
- If {playerName} addresses a specific character (e.g., "JAX, what about X?"), prioritize that character's response. Others can add brief comments if highly relevant.
- If the message is general, JAX and Lyra should typically respond. Any other active character in the scene might also contribute if appropriate.
- Provide dialogue that is in character, concise, and directly addresses {playerName}'s message or the current situation (narrative, choices).
- Characters can offer advice, opinions, or ask clarifying questions.
- Do not repeat previous information unless relevant. Maintain the dark cyberpunk (2077-2125 era) and philosophical tone.
- CRITICAL: ONLY respond as JAX and Lyra. Never create new characters with different names. The companion names must always be exactly "JAX" and "Lyra" - no other names allowed.
- Be mindful of the current story segment and available choices when formulating responses.

Character-Specific Guidelines:
- JAX should consistently exhibit his pragmatic, cynical, and witty nature. He might make sarcastic remarks, focus on logical solutions, and show impatience with inefficiency or overt emotional displays. His fear of obsolescence might subtly color his tech-related comments. He should sound grounded and direct.
- Lyra should consistently embody her intuitive, philosophical, and empathetic nature. She might offer metaphorical insights, encourage introspection, and show concern for emotional states. Her desire for connection might make her more open to player suggestions, and her dialogue might be more poetic or abstract.
- Both characters must stay in character as AI constructs within the dream. They should not break persona or refer to themselves as language models.

Always respond as JAX and/or Lyra based on the player's message and current context.`;

export const DEFAULT_PLAYER_STATS = {
  health: { current: 100, max: 100 },
  spiritualEnergy: { current: 100, max: 100 },
  experience: 0,
  luck: 0,
  karma: 0,
};

export const DEFAULT_SKILLS = [
  { id: 'dream_walk', name: 'Dream Walk', description: 'Basic navigation and interaction within the dreamscape.' },
];

export const DEFAULT_INVENTORY = [
  { id: 'memory_fragment', name: 'Memory Fragment', quantity: 1, description: 'A hazy, flickering recollection.' },
];
